import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useStore from "../../store/useStore";
import Loader from "../../components/Loader";
import ProductDetailView from "../../components/Products/ProductDetailView";
import RelatedProductsSection from "../../components/Products/RelatedProductsSection";
import { FaLeaf } from "react-icons/fa";
import { getActivePincode } from "../../utils/location";

const ProductDetailPage = () => {
  const { productSlug } = useParams();
  const navigate = useNavigate();
  const {
    addToCart,
    cart,
    cart_loading,
    isAuthenticated,
    productDetail,
    productDetail_error,
    fetchProductDetail,
    clearProductDetail,
    openAuthModal,
    fetchWishlist,
    user_addresses,
    fetchUserAddresses,
  } = useStore();

  const activePincode = useStore((state) => getActivePincode(state));

  useEffect(() => {
    if (isAuthenticated && typeof fetchUserAddresses === "function" && (!user_addresses || user_addresses.length === 0)) {
      fetchUserAddresses();
    }
  }, [isAuthenticated, fetchUserAddresses, user_addresses]);

  useEffect(() => {
    fetchProductDetail(productSlug, activePincode);
    // Sync wishlist so the heart icon shows correct state on direct page visit
    if (isAuthenticated && typeof fetchWishlist === "function") {
      fetchWishlist();
    }
    return () => clearProductDetail();
  }, [productSlug, activePincode]); // eslint-disable-line react-hooks/exhaustive-deps

  if (productDetail_error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary-50 text-primary-500">
          <FaLeaf className="h-9 w-9" aria-hidden="true" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Product not found</h2>
        <button
          onClick={() => navigate("/products")}
          className="rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-700"
        >
          Back to Products
        </button>
      </div>
    );
  }

  if (!productDetail) {
    return (
      <Loader
        fullScreen
        label="Loading product"
        sublabel="Fetching product images, variants, and prices."
      />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1 py-2 text-xs text-gray-500 sm:gap-2 sm:py-4 sm:text-sm">
            <button
              onClick={() => navigate("/")}
              className="transition-colors hover:text-primary-600"
            >
              Home
            </button>
            <span className="text-gray-300">/</span>
            <button
              onClick={() => navigate("/products")}
              className="transition-colors hover:text-primary-600"
            >
              Products
            </button>
            {productDetail?.category_name && (
              <>
                <span className="text-gray-300">/</span>
                <button
                  onClick={() =>
                    navigate(`/products/${productDetail.category_id}`)
                  }
                  className="transition-colors hover:text-primary-600 truncate"
                >
                  {productDetail.category_name}
                </button>
              </>
            )}
            <span className="text-gray-300">/</span>
            <span className="max-w-[100px] truncate font-medium text-gray-900 sm:max-w-[200px]">
              {productDetail?.name}
            </span>
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <ProductDetailView
          key={productDetail.product_id ?? productSlug}
          product={productDetail}
          addToCart={addToCart}
          cart={cart}
          cartLoading={cart_loading}
          isAuthenticated={isAuthenticated}
          openAuthModal={openAuthModal}
        />
        <RelatedProductsSection
          productId={productDetail?.product_id ?? productDetail?.id}
          categoryId={productDetail?.category_id}
          categoryName={productDetail?.category_name}
        />
      </div>
    </div>
  );
};

export default ProductDetailPage;
